import React, { useState } from 'react';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { 
  Clock,
  Users,
  MessageCircle,
  DollarSign,
  TrendingUp,
  CheckCircle,
  ArrowRight,
  Calendar,
  Mail,
  Download,
  Phone,
  Building,
  Timer,
  Zap,
  Target,
  BarChart3
} from 'lucide-react';

const QuizPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: '',
    companyName: '',
    employees: '',
    advisors: '',
    queriesPerEmployee: '',
    resolutionTime: '',
    waitTime: '',
    productivityImpact: '',
    advisorHourlyRate: '',
    monthlyBudget: '',
    email: ''
  });
  const [results, setResults] = useState(null);

  const questions = [
    {
      id: 'contact',
      title: 'Let\'s Get Started',
      subtitle: 'Help us personalize your VioQbase assessment',
      fields: [
        { key: 'firstName', label: 'First Name', type: 'text', placeholder: 'Enter your first name' },
        { key: 'companyName', label: 'Company Name', type: 'text', placeholder: 'Enter your company name' },
        { key: 'email', label: 'Work Email', type: 'email', placeholder: 'your.email@company.com' }
      ]
    },
    {
      id: 'company-size',
      title: 'Tell Us About Your Team',
      subtitle: 'Understanding your company size helps us calculate accurate savings',
      fields: [
        { 
          key: 'employees', 
          label: 'How many employees does your company have?', 
          type: 'select',
          options: [
            { value: '50', label: '1-50 employees' },
            { value: '150', label: '51-150 employees' },
            { value: '500', label: '151-500 employees' },
            { value: '1000', label: '501-1000 employees' },
            { value: '2000', label: '1000+ employees' }
          ]
        },
        { 
          key: 'advisors', 
          label: 'How many HR/IT advisors handle employee questions?', 
          type: 'select',
          options: [
            { value: '2', label: '1-2 advisors' },
            { value: '5', label: '3-5 advisors' },
            { value: '10', label: '6-10 advisors' },
            { value: '20', label: '11-20 advisors' },
            { value: '30', label: '20+ advisors' }
          ]
        }
      ]
    },
    {
      id: 'query-volume',
      title: 'Current Query Patterns',
      subtitle: 'Let\'s understand your current support volume',
      fields: [
        { 
          key: 'queriesPerEmployee', 
          label: 'On average, how many questions does each employee ask advisors per month?', 
          type: 'select',
          options: [
            { value: '2', label: '1-2 questions' },
            { value: '5', label: '3-5 questions' },
            { value: '8', label: '6-8 questions' },
            { value: '12', label: '9-12 questions' },
            { value: '15', label: '12+ questions' }
          ]
        },
        { 
          key: 'resolutionTime', 
          label: 'How long does it typically take an advisor to resolve a routine question?', 
          type: 'select',
          options: [
            { value: '5', label: '1-5 minutes' },
            { value: '10', label: '6-10 minutes' },
            { value: '15', label: '11-15 minutes' },
            { value: '20', label: '16-20 minutes' },
            { value: '30', label: '20+ minutes' }
          ]
        }
      ]
    },
    {
      id: 'wait-times',
      title: 'Employee Experience',
      subtitle: 'Understanding delays helps us calculate productivity impact',
      fields: [
        { 
          key: 'waitTime', 
          label: 'How long do employees typically wait for answers to routine questions?', 
          type: 'select',
          options: [
            { value: '0.5', label: '30 minutes or less' },
            { value: '2', label: '1-2 hours' },
            { value: '6', label: '3-6 hours' },
            { value: '12', label: '6-12 hours' },
            { value: '24', label: '1+ days' }
          ]
        },
        { 
          key: 'productivityImpact', 
          label: 'What percentage of productivity is lost while employees wait for answers?', 
          type: 'select',
          options: [
            { value: '10', label: '10% - Minor slowdown' },
            { value: '25', label: '25% - Some blocked work' },
            { value: '50', label: '50% - Significant delays' },
            { value: '75', label: '75% - Major productivity loss' },
            { value: '100', label: '100% - Complete work stoppage' }
          ]
        }
      ]
    },
    {
      id: 'costs',
      title: 'Financial Information',
      subtitle: 'This helps us calculate your potential ROI',
      fields: [
        { 
          key: 'advisorHourlyRate', 
          label: 'What\'s the approximate hourly rate of your advisors (including benefits)?', 
          type: 'select',
          options: [
            { value: '25', label: '£20-30 per hour' },
            { value: '35', label: '£30-40 per hour' },
            { value: '45', label: '£40-50 per hour' },
            { value: '55', label: '£50-60 per hour' },
            { value: '65', label: '£60+ per hour' }
          ]
        },
        { 
          key: 'monthlyBudget', 
          label: 'What\'s your current monthly budget for employee support solutions?', 
          type: 'select',
          options: [
            { value: '500', label: '£0-500' },
            { value: '1500', label: '£500-1,500' },
            { value: '3000', label: '£1,500-3,000' },
            { value: '5000', label: '£3,000-5,000' },
            { value: '7500', label: '£5,000+' }
          ]
        }
      ]
    }
  ];

  const calculateResults = () => {
    const employees = parseInt(formData.employees);
    const advisors = parseInt(formData.advisors);
    const queriesPerEmployee = parseInt(formData.queriesPerEmployee);
    const resolutionTimeMinutes = parseInt(formData.resolutionTime);
    const waitTimeHours = parseFloat(formData.waitTime);
    const productivityImpact = parseInt(formData.productivityImpact);
    const advisorHourlyRate = parseInt(formData.advisorHourlyRate);
    const monthlyBudget = parseInt(formData.monthlyBudget);

    // Constants
    const automationRate = 0.75;
    const employeeHourlyRate = 22;
    const vioqbaseMonthlyFee = employees > 1000 ? 2500 : employees > 500 ? 1500 : employees > 150 ? 800 : 400;

    // Calculations
    const totalMonthlyQueries = employees * queriesPerEmployee;
    const automatedQueries = totalMonthlyQueries * automationRate;
    const currentAdvisorHours = (totalMonthlyQueries * resolutionTimeMinutes) / 60;
    const newAdvisorHours = ((totalMonthlyQueries - automatedQueries) * resolutionTimeMinutes) / 60;
    const advisorHoursSaved = currentAdvisorHours - newAdvisorHours;
    const advisorCostSavings = advisorHoursSaved * advisorHourlyRate;

    const productivityHoursSaved = automatedQueries * waitTimeHours * (productivityImpact / 100);
    const productivityValue = productivityHoursSaved * employeeHourlyRate;

    const totalMonthlyValue = advisorCostSavings + productivityValue;
    const netMonthlySavings = totalMonthlyValue - vioqbaseMonthlyFee;
    const annualSavings = netMonthlySavings * 12;
    const totalTimeSaved = advisorHoursSaved + productivityHoursSaved;
    const workingDaysEquivalent = Math.round(totalTimeSaved / 8);

    return {
      advisorHoursSaved: Math.round(advisorHoursSaved),
      productivityHoursSaved: Math.round(productivityHoursSaved),
      totalTimeSaved: Math.round(totalTimeSaved),
      workingDaysEquivalent,
      advisorCostSavings: Math.round(advisorCostSavings),
      productivityValue: Math.round(productivityValue),
      totalMonthlyValue: Math.round(totalMonthlyValue),
      vioqbaseMonthlyFee,
      netMonthlySavings: Math.round(netMonthlySavings),
      annualSavings: Math.round(annualSavings),
      automationRate: Math.round(automationRate * 100),
      speedImprovement: waitTimeHours > 0 ? Math.round(waitTimeHours * 60) : 'instant',
      currentMonthlyCost: Math.round(currentAdvisorHours * advisorHourlyRate),
      totalMonthlyQueries,
      automatedQueries: Math.round(automatedQueries)
    };
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const calculatedResults = calculateResults();
      setResults(calculatedResults);
      setCurrentStep(questions.length); // Move to results page
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioqbase-demo', '_blank');
  };

  const handleDownloadReport = () => {
    alert('PDF report download will be implemented with backend!');
  };

  const handleContactSales = () => {
    alert('Contact sales functionality will be implemented with backend!');
  };

  const isStepComplete = () => {
    const currentQuestion = questions[currentStep];
    return currentQuestion.fields.every(field => formData[field.key] && formData[field.key].trim() !== '');
  };

  if (results) {
    return (
      <div className="min-h-screen bg-white font-sans">
        <Navigation />
        
        {/* Hero Results Section */}
        <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
          <div className="relative container mx-auto px-6 text-center">
            <div className="max-w-4xl mx-auto space-y-8">
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight tracking-tight">
                🎉 Great News, <span className="text-[#ff7a1f]">{formData.firstName}</span>!
              </h1>
              <h2 className="text-2xl lg:text-3xl font-semibold text-blue-100">
                VioQbase can save {formData.companyName}:
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
                  <CardContent className="p-6">
                    <DollarSign className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                    <div className="text-3xl font-bold text-white mb-2">£{results.netMonthlySavings.toLocaleString()}</div>
                    <div className="text-blue-200">per month</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
                  <CardContent className="p-6">
                    <Clock className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                    <div className="text-3xl font-bold text-white mb-2">{results.totalTimeSaved}</div>
                    <div className="text-blue-200">hours per month</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur border-white/20 text-center">
                  <CardContent className="p-6">
                    <TrendingUp className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                    <div className="text-3xl font-bold text-white mb-2">{results.automationRate}%</div>
                    <div className="text-blue-200">productivity boost</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Detailed Results Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-7xl mx-auto space-y-16">
              
              {/* Time Savings Section */}
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-[#112d56] mb-6">
                  🕒 Time VioQbase Will Save {formData.companyName}
                </h2>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="text-center pb-4">
                    <Timer className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                    <CardTitle className="text-xl font-bold text-[#112d56]">Monthly Advisor Hours Saved</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="text-4xl font-bold text-[#112d56] mb-2">{results.advisorHoursSaved}</div>
                    <div className="text-gray-600">hours per month</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="text-center pb-4">
                    <Users className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                    <CardTitle className="text-xl font-bold text-[#112d56]">Employee Productivity Hours</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="text-4xl font-bold text-[#112d56] mb-2">{results.productivityHoursSaved}</div>
                    <div className="text-gray-600">hours per month</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="text-center pb-4">
                    <Calendar className="h-12 w-12 text-[#ff7a1f] mx-auto mb-4" />
                    <CardTitle className="text-xl font-bold text-[#112d56]">Annual Time Savings</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="text-4xl font-bold text-[#112d56] mb-2">{results.totalTimeSaved * 12}</div>
                    <div className="text-gray-600">hours per year</div>
                  </CardContent>
                </Card>
              </div>
              
              <div className="text-center bg-[#ff7a1f]/10 rounded-2xl p-8 mb-16">
                <p className="text-xl font-semibold text-[#112d56]">
                  That's equivalent to <span className="text-[#ff7a1f] font-bold">{results.workingDaysEquivalent} full working days</span> returned to your team every month
                </p>
              </div>

              {/* Financial Savings Section */}
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-[#112d56] mb-6">
                  💸 Money VioQbase Will Save {formData.companyName}
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <DollarSign className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">£{results.advisorCostSavings.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Monthly Advisor Savings</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <TrendingUp className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">£{results.productivityValue.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Monthly Productivity Value</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg bg-[#ff7a1f]/10">
                  <CardContent className="p-6 text-center">
                    <Target className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">£{results.netMonthlySavings.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Total Monthly Savings</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg bg-green-50">
                  <CardContent className="p-6 text-center">
                    <BarChart3 className="h-8 w-8 text-green-600 mx-auto mb-3" />
                    <div className="text-2xl font-bold text-green-600 mb-2">£{results.annualSavings.toLocaleString()}</div>
                    <div className="text-sm text-gray-600">Annual Impact</div>
                  </CardContent>
                </Card>
              </div>

              {/* Investment Comparison */}
              <Card className="border-0 shadow-xl mb-16">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] text-center">Investment Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6 text-center">
                    <div className="p-4">
                      <div className="text-lg font-semibold text-gray-700 mb-2">VioQbase Monthly Cost</div>
                      <div className="text-2xl font-bold text-[#ff7a1f]">£{results.vioqbaseMonthlyFee.toLocaleString()}</div>
                    </div>
                    <div className="p-4">
                      <div className="text-lg font-semibold text-gray-700 mb-2">Your Current Monthly Cost</div>
                      <div className="text-2xl font-bold text-red-600">£{results.currentMonthlyCost.toLocaleString()}</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <div className="text-lg font-semibold text-gray-700 mb-2">Net Monthly Savings</div>
                      <div className="text-3xl font-bold text-green-600">£{results.netMonthlySavings.toLocaleString()}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Workplace Productivity Section */}
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-[#112d56] mb-6">
                  📈 How VioQbase Increases {formData.companyName}'s Productivity
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <Zap className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">{results.automationRate}%</div>
                    <div className="text-sm text-gray-600">Questions Answered Instantly</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <Timer className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">From {formData.waitTime}h to 0</div>
                    <div className="text-sm text-gray-600">Wait Time Reduced</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <Users className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">{results.advisorHoursSaved}h</div>
                    <div className="text-sm text-gray-600">Advisor Hours Freed</div>
                  </CardContent>
                </Card>
                
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <MessageCircle className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <div className="text-2xl font-bold text-[#112d56] mb-2">{results.speedImprovement === 'instant' ? '∞' : results.speedImprovement}x</div>
                    <div className="text-sm text-gray-600">Faster Responses</div>
                  </CardContent>
                </Card>
              </div>

              {/* Before/After Comparison */}
              <Card className="border-0 shadow-xl bg-gradient-to-r from-gray-50 to-white mb-16">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] text-center">Before vs After VioQbase</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="bg-red-50 p-6 rounded-lg">
                      <h3 className="text-xl font-bold text-red-700 mb-4">Current State</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• {formData.employees} employees asking {results.totalMonthlyQueries.toLocaleString()} questions monthly</li>
                        <li>• Advisors spending {Math.round((results.totalMonthlyQueries * parseInt(formData.resolutionTime)) / 60)} hours on routine queries</li>
                        <li>• Employees waiting {formData.waitTime} hours for answers</li>
                        <li>• Monthly cost: £{results.currentMonthlyCost.toLocaleString()}</li>
                      </ul>
                    </div>
                    
                    <div className="bg-green-50 p-6 rounded-lg">
                      <h3 className="text-xl font-bold text-green-700 mb-4">With VioQbase</h3>
                      <ul className="space-y-2 text-gray-700">
                        <li>• {results.automationRate}% of questions automated instantly</li>
                        <li>• Advisors focus on complex issues only</li>
                        <li>• Zero wait time for routine questions</li>
                        <li>• Monthly cost: £{results.vioqbaseMonthlyFee.toLocaleString()} (including VioQbase)</li>
                        <li className="font-bold text-green-600">• Net benefit: £{results.netMonthlySavings.toLocaleString()} saved + {results.totalTimeSaved} hours returned</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Call to Action */}
              <Card className="border-0 shadow-2xl bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white overflow-hidden">
                <CardContent className="p-12 text-center relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                  <div className="relative z-10">
                    <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                      Ready to unlock these savings for <span className="text-[#ff7a1f]">{formData.companyName}</span>?
                    </h3>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                      Join 500+ companies already saving time and money with VioQbase
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        onClick={handleDemo}
                        size="lg"
                        className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                      >
                        <Calendar className="mr-2 h-5 w-5" />
                        Contact Us
                      </Button>
                      <Button
                        onClick={handleDownloadReport}
                        size="lg"
                        variant="outline"
                        className="border-white text-white hover:bg-white hover:text-[#112d56] font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300"
                      >
                        <Download className="mr-2 h-5 w-5" />
                        Download Report
                      </Button>
                      <Button
                        onClick={handleContactSales}
                        size="lg"
                        variant="outline"
                        className="border-white text-white hover:bg-white hover:text-[#112d56] font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300"
                      >
                        <Phone className="mr-2 h-5 w-5" />
                        Contact Sales
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Quiz Header */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-16">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
              Discover Your <span className="text-[#ff7a1f]">VioQbase Savings</span>
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Get a personalized assessment of how much time and money VioQbase can save your company
            </p>
            
            {/* Demo Results Link */}
            <div className="mt-6">
              <a 
                href="/quiz-results" 
                className="inline-flex items-center text-[#ff7a1f] hover:text-[#e6681b] font-semibold text-lg transition-colors"
              >
                👀 See Sample Results
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </div>
            
            {/* Progress Bar */}
            <div className="max-w-md mx-auto mt-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-blue-200">Progress</span>
                <span className="text-sm text-blue-200">{currentStep + 1} of {questions.length}</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-[#ff7a1f] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / questions.length) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quiz Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <Card className="border-0 shadow-2xl">
              <CardHeader className="text-center pb-6">
                <CardTitle className="text-2xl lg:text-3xl font-bold text-[#112d56] mb-4">
                  {currentQuestion.title}
                </CardTitle>
                <CardDescription className="text-lg text-gray-600">
                  {currentQuestion.subtitle}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {currentQuestion.fields.map((field, index) => (
                  <div key={field.key} className="space-y-3">
                    <label className="block text-sm font-semibold text-[#112d56]">
                      {field.label}
                    </label>
                    
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.key]}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff7a1f] text-gray-700"
                      >
                        <option value="">Select an option...</option>
                        {field.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type}
                        value={formData[field.key]}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ff7a1f] text-gray-700"
                      />
                    )}
                  </div>
                ))}
                
                <div className="flex justify-between pt-6">
                  {currentStep > 0 && (
                    <Button
                      onClick={handleBack}
                      variant="outline"
                      className="border-[#112d56] text-[#112d56] hover:bg-[#112d56] hover:text-white px-6 py-3"
                    >
                      Back
                    </Button>
                  )}
                  
                  <Button
                    onClick={handleNext}
                    disabled={!isStepComplete()}
                    className={`ml-auto px-8 py-3 font-semibold rounded-lg transition-all duration-300 ${
                      isStepComplete()
                        ? 'bg-[#ff7a1f] hover:bg-[#e6681b] text-white'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {currentStep === questions.length - 1 ? 'Calculate My Savings' : 'Next'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuizPage;